import { $lang, getTagFromNode, useMeasurementUnitsList, useTranslation } from '@msdk/common'
import {
  ControlValidationType,
  FormFieldDefinition,
  FormOpenMode,
  getConversionUM,
  getSafeValueLimits,
  getSeparator,
  NumericFieldDefinition,
  NumericValue,
  tryConvert,
  tryConvertDecimalInfo,
} from '@msdk/definitions'
import { FormField, FormFieldLabel, FormHelperText, InputAdornment, TextInput } from '@msdk/ui'
import React from 'react'
import { NumberFormatValues, NumericFormat, SourceInfo } from 'react-number-format'
import { useParams } from 'react-router-dom'
import { useRecoilValue } from 'recoil'

import { useFieldLabel, useFieldSeverity, useFieldValue, useHasMandatoryValidation } from '../../../hooks'
import { FieldProps } from '../../../models/form'
import { nodeHasFlag } from '../../../utils'
import { ValidationMessages } from '../../ValidationMessages'

// max allowed number the user can input, this is here
// to prevent overflows with conversion or formatting calculations
// but the user realistically should never input a number this high inside
// a number field, we would incur in other precision issues anyway.
const MAX_ALLOWED_NUMBER = 899999999999999

export function NumericField({ node, onChange, fieldArgs }: FieldProps) {
  const fieldDef = node.value.fieldDefinition as NumericFieldDefinition
  const formDef = node.value.formDefinition as FormFieldDefinition
  const fieldLabel = useFieldLabel(fieldDef, formDef)
  const [value, setValue] = useFieldValue(node, onChange, 10)
  const lang = useRecoilValue($lang)
  const t = useTranslation()
  const { moduleName } = useParams()
  const mus = useMeasurementUnitsList()

  const hasMandatoryFlag = nodeHasFlag(node, ControlValidationType.Mandatory)
  const hasMandatory = useHasMandatoryValidation(node, fieldDef.entityName)

  const onFieldChange = (values: NumberFormatValues, sourceInfo: SourceInfo) => {
    const newValue = values.floatValue
    if (sourceInfo.event) {
      const newValueDefined = newValue !== null && newValue !== undefined
      const fieldValue = newValueDefined ? new NumericValue(newValue, getMuKey()) : null
      node.value.isDirty = true
      setValue(fieldValue)
    }
  }

  const getDecimalInfo = () => {
    return tryConvertDecimalInfo(fieldDef.decimalInfo, fieldDef.um, fieldDef.entityName, fieldDef.name, fieldDef.step)
  }

  const getRangeLabel = () => {
    let label = ''
    if (node.value.range) {
      const minDefined = node.value.range.min !== null && node.value.range.min !== undefined
      const maxDefined = node.value.range.max !== null && node.value.range.max !== undefined

      const decimals = getDecimalInfo()?.decimalLimit ?? 0
      let convertedMin = minDefined
        ? tryConvert(node.value.range.min, fieldDef.um, fieldDef.entityName, fieldDef.name)
        : undefined
      let convertedMax = maxDefined
        ? tryConvert(node.value.range.max, fieldDef.um, fieldDef.entityName, fieldDef.name)
        : undefined

      // round to decimals here because numericFormatter in toStringForView truncates the number
      convertedMin =
        convertedMin !== null && convertedMin !== undefined ? parseFloat(convertedMin.toFixed(decimals)) : undefined
      convertedMax =
        convertedMax !== null && convertedMax !== undefined ? parseFloat(convertedMax.toFixed(decimals)) : undefined

      const convertedMinNumeric = new NumericValue(convertedMin, undefined, undefined, decimals)
      const convertedMaxNumeric = new NumericValue(convertedMax, undefined, undefined, decimals)

      if (minDefined && maxDefined) {
        label = `(${convertedMinNumeric.toStringForView(lang)} - ${convertedMaxNumeric.toStringForView(lang)})`
      } else if (minDefined && !maxDefined) {
        label = `(> ${convertedMinNumeric.toStringForView(lang)})`
      } else if (!minDefined && maxDefined) {
        label = `(< ${convertedMaxNumeric.toStringForView(lang)})`
      }
    }
    return label
  }

  const getMuKey = () => {
    const conversionUm = getConversionUM(fieldDef.um, fieldDef.entityName, fieldDef.name)
    return conversionUm ?? fieldDef.um
  }

  const getMuIDML = () => {
    const key = getMuKey()
    const muIDML = mus?.find((u) => u.key === key)?.idml ?? key
    return muIDML
  }

  const safeValueLimits =
    fieldDef.safeValueLimits ?? getSafeValueLimits(fieldDef.dbType, fieldDef.precision, fieldDef.scale)

  const maxAllowedNumber = Math.min(
    MAX_ALLOWED_NUMBER,
    tryConvert(safeValueLimits.max, fieldDef.um, fieldDef.entityName, fieldDef.name),
  )
  const minAllowerNumber = Math.max(
    -MAX_ALLOWED_NUMBER,
    tryConvert(safeValueLimits.min, fieldDef.um, fieldDef.entityName, fieldDef.name),
  )

  const validateInput = (values: NumberFormatValues) => {
    return !values.floatValue || (values.floatValue <= maxAllowedNumber && values.floatValue >= minAllowerNumber)
  }

  const isReadOnly = node.value.state === 'Readonly'
  const severity = useFieldSeverity(fieldDef, node)

  return (
    <FormField
      required={(hasMandatory || hasMandatoryFlag) && fieldArgs?.formOpenMode !== FormOpenMode.Readonly}
      size="small"
      data-test-id={getTagFromNode(fieldDef, moduleName)}
      severity={severity}
      readOnly={isReadOnly}
      disabled={isReadOnly}
    >
      {fieldArgs?.showLabel && <FormFieldLabel>{fieldLabel}</FormFieldLabel>}
      <NumericFormat
        label={fieldArgs?.showLabel ? fieldLabel : undefined}
        value={value ? (value.value as number) : ''}
        allowLeadingZeros
        allowNegative
        customInput={TextInput}
        decimalScale={getDecimalInfo()?.decimalLimit}
        decimalSeparator={getSeparator(lang, 'decimal', value ? (value.value as number) : undefined)}
        thousandSeparator={getSeparator(lang, 'group', value ? (value.value as number) : undefined)}
        onValueChange={onFieldChange}
        disabled={isReadOnly}
        placeholder={fieldArgs?.showLabel ? t('CMPS_TYPE_HERE_PLACEHOLDER', 'Type here') : undefined}
        isAllowed={validateInput}
        endAdornment={
          <InputAdornment position="end">
            <FormHelperText>
              {t(getMuIDML(), getMuKey())}
              {getRangeLabel()}
            </FormHelperText>
          </InputAdornment>
        }
      />
      <ValidationMessages messages={node.value.validationMessages} fieldDef={fieldDef} node={node} />
    </FormField>
  )
}
